/**
 * MentionAutocomplete — input enrichi avec dropdown @mention.
 *
 * Détecte "@" dans le texte, ouvre un dropdown avec les users
 * filtrés par le handle tapé (préfixe). Tab / Enter pour insérer.
 *
 * Usage :
 *   <MentionAutocomplete value={body} onChange={setBody} onSubmit={submit} />
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api-client';

type TeamMember = {
  id: string;
  email: string;
  display_name: string | null;
};

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
};

export function MentionAutocomplete({
  value,
  onChange,
  onSubmit,
  placeholder,
  className,
  disabled,
  maxLength,
}: Props) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretAtRef = useRef<number>(-1); // position du @ qui a ouvert le dropdown

  // Charge la team une fois au mount (utilise X-Domain-Slug courant)
  useEffect(() => {
    api
      .get<{ items: TeamMember[] }>('/api/users/me/team')
      .then((d) => setTeam(d.items ?? []))
      .catch(() => setTeam([]));
  }, []);

  // Calcule query courante en fonction du caret position
  const recomputeQuery = useCallback(
    (text: string, caret: number): { open: boolean; q: string; atPos: number } => {
      // Cherche le dernier @ avant le caret, sans espace/tab entre @ et caret
      let i = caret - 1;
      while (i >= 0 && /[a-zA-Z0-9._@-]/.test(text[i]!)) i--;
      if (i < 0 || text[i] !== '@') return { open: false, q: '', atPos: -1 };
      // Vérifie que le @ est en début ou après séparateur
      if (i > 0 && !/[\s,;:!?(\[]/.test(text[i - 1]!)) return { open: false, q: '', atPos: -1 };
      const q = text.slice(i + 1, caret);
      return { open: true, q, atPos: i };
    },
    [],
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    onChange(text);
    const caret = e.target.selectionStart ?? text.length;
    const { open: o, q, atPos } = recomputeQuery(text, caret);
    setOpen(o);
    setQuery(q);
    caretAtRef.current = atPos;
    setSelectedIdx(0);
  }

  function insertMention(user: TeamMember) {
    if (caretAtRef.current < 0 || !inputRef.current) return;
    const text = value;
    const handle = (user.email.split('@')[0] ?? user.email).toLowerCase();
    const before = text.slice(0, caretAtRef.current);
    const after = text.slice((inputRef.current.selectionStart ?? text.length));
    const inserted = `@${handle} `;
    const newText = before + inserted + after;
    onChange(newText);
    setOpen(false);
    // Remet le caret après la mention
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const pos = before.length + inserted.length;
        inputRef.current.selectionStart = pos;
        inputRef.current.selectionEnd = pos;
        inputRef.current.focus();
      }
    });
  }

  const filtered = open
    ? team
        .filter((u) => {
          const handle = u.email.split('@')[0]?.toLowerCase() ?? '';
          const q = query.toLowerCase();
          return (
            handle.startsWith(q) ||
            u.email.toLowerCase().startsWith(q) ||
            (u.display_name ?? '').toLowerCase().includes(q)
          );
        })
        .slice(0, 8)
    : [];

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (open && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((i) => (i + 1) % filtered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        insertMention(filtered[selectedIdx]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' && onSubmit && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={className ?? 'w-full border rounded px-2 py-1 text-sm'}
      />
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-20 left-0 top-full mt-1 min-w-[240px] max-w-[320px] bg-white dark:bg-gray-800 border dark:border-gray-700 rounded shadow-lg overflow-hidden"
          role="listbox"
        >
          {filtered.map((u, idx) => (
            <li
              key={u.id}
              role="option"
              aria-selected={idx === selectedIdx}
              onMouseDown={(e) => {
                // onMouseDown au lieu de onClick pour empêcher le blur avant
                e.preventDefault();
                insertMention(u);
              }}
              className={`px-3 py-1.5 text-sm cursor-pointer ${
                idx === selectedIdx
                  ? 'bg-primary-500 text-white'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="font-medium">
                @{u.email.split('@')[0]}
              </div>
              <div className="text-xs opacity-75 truncate">
                {u.display_name ?? u.email}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
